import React, { PureComponent } from 'react';
import TimeAgo from 'react-timeago';
import { connect } from 'react-redux';
import Radium from 'radium';
import { injectIntl, intlShape } from 'react-intl';
import * as keyboardEventHelpers from 'globals/keyboardEvents';

import { Session } from 'models';
import Avatar from 'shared/components/Avatar';
import MarkdownMessage from 'shared/components/MarkdownMessage';

import { handleJoinConversation } from '../../app/ChatApp/actions_routing';
import {
  makeGetFirstMessageInSession,
  makeGetUnreadCountsForSession,
  makeGetSessionUserName,
  makeGetSessionAvatarUrl,
  makeGetSessionLastMessageTimeUTC,
  getCompanyClosableChatEnabled,
} from '../../app/ChatApp/selectors';

import ChatHistoryItemAnon from '../ChatHistoryItemAnon';

import styles from './styles';
import messages from './messages';

// todo do we use react-timeago's intl or react-intls?
const formatter = (value, u, suffix) => {
  let unit = u;
  if (value < 60 && unit === 'second' && suffix === 'ago') {
    return 'A few seconds ago';
  }

  if (value !== 1) {
    unit += 's';
  }

  return `${value} ${unit} ${suffix}`;
};

export class ChatHistoryItem extends PureComponent {
  handleClick = e => {
    const { item, onClick } = this.props;

    e.preventDefault();
    onClick(item);
  };

  handleKeyPress = e => {
    const { item, onClick } = this.props;

    if (keyboardEventHelpers.isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();

      onClick(item);
    }
  };

  renderTimeAgo() {
    const { intl, lastMessageTimeUTC, isLocked } = this.props;

    if (!lastMessageTimeUTC) {
      return null;
    }

    if (isLocked) {
      return (
        <span style={styles.closedLabel} className="kustomer-app-list-item-closed-label">
          {intl.formatMessage(messages.closed)}
        </span>
      );
    }

    // reit above comment: either transition to react-intl or adapt timeago for intl support
    return <TimeAgo date={lastMessageTimeUTC} minPeriod={60} formatter={formatter} />;
  }

  renderUnreadCount() {
    const { unreadCount, intl } = this.props;

    if (!unreadCount) return null;

    return (
      <div
        aria-live="assertive"
        aria-label={intl.formatMessage(messages.aria_label_unread_messages, { unreadMessages: unreadCount })}
        className="unread kustomer-app-list-item-unread-count"
        style={styles.unread}
      >
        {unreadCount}
      </div>
    );
  }

  renderAvatar() {
    const { userName, avatarUrl } = this.props;

    return <Avatar defaultName={userName} avatarUrl={avatarUrl} size={40} />;
  }

  renderNameDateRow() {
    const { userName } = this.props;

    return (
      <div style={styles.nameDateRow}>
        <span style={styles.name} className="kustomer-app-list-item-name">
          {userName}
        </span>
        <span style={styles.date} aria-live="polite" className="kustomer-app-list-item-date">
          {this.renderTimeAgo()}
        </span>
      </div>
    );
  }

  renderMessageRow() {
    const { intl, item } = this.props;

    if (!item.preview) return <div style={styles.messageRow}>{intl.formatMessage(messages.no_preview)}</div>;

    return (
      <div
        className="messageRow messagePreview kustomer-app-list-item-message-preview"
        aria-label={item.preview}
        style={styles.messageRow}
      >
        <MarkdownMessage text={item.preview} isPreview />
      </div>
    );
  }

  renderLockedOverlay = () => {
    const { isLocked } = this.props;

    if (!isLocked) return null;

    return <div style={styles.locked} />;
  };

  render() {
    const { item, isLocked } = this.props;

    if (item.type === 'anon') return <ChatHistoryItemAnon />;

    const parentClassName = isLocked
      ? 'kustomer-app-list-item kustomer-app-list-item-disabled'
      : 'kustomer-app-list-item';

    return (
      <div
        tabIndex="0"
        style={[styles.wrapper, styles.wrapperHover]}
        onKeyPress={this.handleKeyPress}
        onClick={this.handleClick}
        className={parentClassName}
        role="treeitem"
      >
        {this.renderLockedOverlay()}
        {this.renderUnreadCount()}
        {this.renderAvatar()}
        <div className="messageColumn" style={styles.messageColumn}>
          {this.renderNameDateRow()}
          {this.renderMessageRow()}
        </div>
      </div>
    );
  }
}

ChatHistoryItem.propTypes = {
  item: React.PropTypes.instanceOf(Session),
  onClick: React.PropTypes.func,
  unreadCount: React.PropTypes.number,
  userName: React.PropTypes.string,
  avatarUrl: React.PropTypes.string,
  lastMessageTimeUTC: React.PropTypes.number,
  intl: intlShape.isRequired,
  isLocked: React.PropTypes.bool,
};

ChatHistoryItem.defaultProps = {
  item: Session(),
  unreadCount: 0,
  onClick: () => {},
  title: 'Untitled',
  lastMessageTimeUTC: null,
  closableChatEnabled: false,
  isLocked: false,
};

export const makeMapStateToProps = () => {
  const getFirstMessageInSession = makeGetFirstMessageInSession();
  const getUnreadCountsForSession = makeGetUnreadCountsForSession();
  const getSessionUserName = makeGetSessionUserName();
  const getSessionAvatarUrl = makeGetSessionAvatarUrl();
  const getSessionLastMessageTimeUTC = makeGetSessionLastMessageTimeUTC();

  return (state, ownProps) => {
    const closableChatEnabled = getCompanyClosableChatEnabled(state);

    return {
      firstMessage: getFirstMessageInSession(state, ownProps),
      unreadCount: getUnreadCountsForSession(state, ownProps),
      userName: getSessionUserName(state, ownProps),
      avatarUrl: getSessionAvatarUrl(state, ownProps),
      lastMessageTimeUTC: getSessionLastMessageTimeUTC(state, ownProps),
      isLocked: closableChatEnabled && !!ownProps.item.lockedAt,
    };
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    onClick: item => {
      if (item) {
        dispatch(handleJoinConversation(item));
      }
    },
  };
};

export const intlComponent = injectIntl(Radium(ChatHistoryItem), { withRef: true });

export default connect(makeMapStateToProps, mapDispatchToProps)(intlComponent);
